// packages/shared/src/ledger/types.ts
//
// Ledger domain TS interfaces + Zod schemas — Account, LedgerEntry, TxGroup.
// Implements journal model with AccountType taxonomy.
//
// Node-pure (no Fastify / Mongoose). Safe to bundle in Vite client builds.
//
// Design invariants:
//   - All timestamps are unix milliseconds (integer)
//   - All monetary values are integer cents (never floats on the wire)
//   - amountCents is always positive; direction (DR/CR) carries the sign
//   - eventId is a ULID (F-04) — string, not ObjectId
//   - idempotencyKey is caller-provided (F-04); engine rejects duplicates
//
// Naming note: `LedgerCurrencyCode` (not plain `CurrencyCode`) to avoid a
// barrel-level name collision with `CurrencyCode` exported by `currency.ts`.
import { z } from "zod";
import { AccountTypeSchema, AccountStatusSchema, LedgerDirectionSchema, PostingTypeSchema, TxGroupTypeSchema, } from "./enums.js";
// ---------------------------------------------------------------------------
// Primitive helpers
// ---------------------------------------------------------------------------
/**
 * ISO-4217 currency code Zod schema for ledger domain validation.
 * Named `LedgerCurrencyCodeSchema` to avoid collision with the
 * `CurrencyCode` type already exported by `currency.ts` at the barrel root.
 */
export const LedgerCurrencyCodeSchema = z.string().regex(/^[A-Z]{3}$/, {
    message: "Must be a 3-letter ISO-4217 currency code (e.g. USD, EUR)",
});
/** MongoDB ObjectId hex string (24 lowercase hex chars). */
export const ObjectIdHexSchema = z.string().regex(/^[a-f0-9]{24}$/, {
    message: "Must be a 24-character lowercase hex ObjectId",
});
/** ULID string (26 chars, Crockford base32). Used for eventId per F-04. */
export const UlidSchema = z.string().regex(/^[0-9A-HJKMNP-TV-Z]{26}$/, {
    message: "Must be a valid ULID (26 Crockford base32 chars)",
});
/** Unix timestamp in milliseconds (non-negative integer). */
export const UnixMsSchema = z.number().int().nonnegative();
// ---------------------------------------------------------------------------
// Account — per-account ledger node
//
// Cardinality:
//   merchant_wallet / merchant_credit_liability / merchant_invoice_receivable /
//   credit_memo_liability / spending_held / spending_consumed — 1-1 per merchant
//   platform_cash / platform_revenue / fx_gain_loss — global (ownerId = null)
//   tax_payable — 1-N per jurisdiction (ownerId = jurisdictionId)
//   outstanding_debt — 1-N per case (ownerId = caseId)
// ---------------------------------------------------------------------------
export const AccountSchema = z.object({
    /** MongoDB ObjectId of the account document */
    _id: ObjectIdHexSchema,
    /**
     * Entity category that owns this account.
     * "system" covers global accounts (platform_cash, platform_revenue, fx_gain_loss).
     */
    ownerType: z.enum(["merchant", "vendor", "system", "saler"]),
    /**
     * ObjectId of the owning entity.
     * Null for global accounts (ownerType = "system").
     */
    ownerId: ObjectIdHexSchema.nullable(),
    accountType: AccountTypeSchema,
    /** ISO-4217 currency code; defaults to USD (F-01, Phase 1 rail). */
    currency: LedgerCurrencyCodeSchema.default("USD"),
    status: AccountStatusSchema,
    /**
     * Denormalized balance cache in cents (integer).
     * Computed as Σ(CR) - Σ(DR) for CR-normal accounts, Σ(DR) - Σ(CR) for DR-normal.
     * Source-of-truth is always the LedgerEntry rows; this field is a derived view.
     * See BalanceSide in enums.ts for the formula per account type.
     */
    balance: z.number().int(),
    createdAt: UnixMsSchema,
    updatedAt: UnixMsSchema,
});
// ---------------------------------------------------------------------------
// LedgerEntry — immutable double-entry journal line
//
// Each financial event produces exactly 2 entries with opposing directions
// (one DR, one CR) summing to zero — "double-entry lite".
// ---------------------------------------------------------------------------
export const LedgerEntrySchema = z.object({
    /** MongoDB ObjectId of the entry document */
    _id: ObjectIdHexSchema,
    /** Links all entries produced by a single financial operation. */
    txGroupId: ObjectIdHexSchema,
    /** Account being debited or credited. */
    accountId: ObjectIdHexSchema,
    /**
     * DR = debit (increases asset / decreases liability)
     * CR = credit (decreases asset / increases liability)
     */
    direction: LedgerDirectionSchema,
    /**
     * Always a positive integer (cents). The direction field carries the sign.
     * Invariant: amountCents > 0.
     */
    amountCents: z.number().int().positive(),
    /** Must match the currency of the associated account. */
    currency: LedgerCurrencyCodeSchema,
    postingType: PostingTypeSchema,
    /**
     * ULID (F-04) — lexicographically sortable, monotonic, collision-resistant.
     * Identifies the upstream domain event that triggered this posting.
     */
    eventId: UlidSchema,
    /**
     * Caller-supplied idempotency key (F-04).
     * The ledger engine rejects any entry with a duplicate key within the same
     * txGroupId, ensuring safe retry semantics.
     */
    idempotencyKey: z.string().min(1),
    /** When the underlying financial event occurred (may differ from createdAt). */
    occurredAt: UnixMsSchema,
    /** Optional human-readable memo for audit / reconciliation. */
    memo: z.string().nullable(),
    createdAt: UnixMsSchema,
});
// ---------------------------------------------------------------------------
// TxGroup — envelope grouping one or more related LedgerEntry rows
//
// A single merchant action (e.g. ORDER_CHARGE) typically produces a TxGroup
// containing 2-4 LedgerEntry rows (wallet DR, revenue CR, tax CR, etc.).
// ---------------------------------------------------------------------------
export const TxGroupSchema = z.object({
    /** MongoDB ObjectId of the transaction group document */
    _id: ObjectIdHexSchema,
    type: TxGroupTypeSchema,
    /**
     * Merchant that initiated or is primarily affected by this transaction.
     * Null for system-originated groups (e.g. platform-level adjustments).
     */
    merchantId: ObjectIdHexSchema.nullable(),
    /**
     * Arbitrary metadata key-value bag. Standard keys:
     *   trigger: "auto_recharge"        — for INSTANT_TOPUP triggered by balance floor
     *   orderId: "<ObjectIdHex>"        — for ORDER_CHARGE / ORDER_REFUND
     *   transferPairId: "<ObjectIdHex>" — links TRANSFER_SENT ↔ TRANSFER_RECEIVED
     *   chargebackId: "<ObjectIdHex>"   — for CHARGEBACK_RECOVERY
     */
    metadata: z.record(z.string(), z.unknown()).default({}),
    createdAt: UnixMsSchema,
});
//# sourceMappingURL=types.js.map